import { useMemo } from 'react';
import {
  Container,
  DesignTokenSizeEnum,
  Text,
  TextInput,
  TokenTextAppearance,
  TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { useTribalContext } from '@/context/tribal';
import {
  Accordion,
  AccordionGroup,
  Box,
  SectionHeader,
} from '@/modules/components';
import ModelFeaturesSection from '@/modules/features/compare/ModelFeaturesSection/ModelFeaturesSection';
import { formatIdForDOM } from '@/modules/utils/dom';
import { getCategory1AndBespokeSpecItems } from '@/modules/utils/storeUtils';
import useGlobalStore from '@/stores/globalStore';
import { SectionNames } from '@/analytics/sectionNames';
import { useSpecSearch } from '@/hooks/useSpecSearch';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import { isMobile } from '@/modules/utils/breakpoints';
import { Magnifier } from '@volkswagen-onehub/icons-core';

const ModelFeatures = () => {
  const { contentManager, analyticsBuilder, analyticsManager } =
    useTribalContext();
  const { configurations, retailers } = useGlobalStore((state) => state.app);
  const activeSlideIndex = useGlobalStore((state) => state.activeSlideIndex);

  const isMobileWidth = useResizeResponsive(isMobile);

  const modelFeaturesData = useMemo(
    () => getCategory1AndBespokeSpecItems(configurations, contentManager),
    [configurations]
  );

  const analyticsCommonFields = {
    sectionName: SectionNames.COMPARE_PAGE_MODEL_FEATURES,
    analyticsManager,
    analyticsBuilder,
    retailers,
    configurations,
  };

  const {
    searchRef,
    error,
    isSpecDetailOpen,
    renderTextWithHighlights,
    handleSubmitSearch,
    handleClearSearch,
    handleToggleAccordion,
  } = useSpecSearch({
    categories: Object.keys(modelFeaturesData).map((key) => [
      key,
      modelFeaturesData[key],
    ]),
    mode: 'compare',
    analyticsCommonFields,
  });

  return (
    <div>
      <Box
        mb={DesignTokenSizeEnum.static370}
        b960={{
          mb: DesignTokenSizeEnum.static530,
        }}
      >
        <Container
          horizontalAlign="space-between"
          verticalAlign="flex-end"
          gutter={DesignTokenSizeEnum.dynamic0100}
          stretchContent
          wrap="auto"
        >
          <Container wrap="always">
            <Box
              as={SectionHeader}
              title={contentManager.get('model-features-section-title-normal')}
              titleBold={contentManager.get(
                'model-features-section-title-bold'
              )}
              description={contentManager.get(
                'model-features-section-description'
              )}
              titleSize={TokenTextAppearance.headline400}
              descriptionSize={TokenTextAppearance.bigcopy100}
            />
          </Container>
          <Container
            wrap={isMobileWidth ? 'always' : 'auto'}
            horizontalAlign="flex-end"
            verticalAlign="flex-start"
          >
            <Box width={isMobileWidth ? '100%' : '340px'} height={'58px'}>
              <form onSubmit={handleSubmitSearch}>
                <TextInput
                  label="Search"
                  isFloating
                  type="search"
                  ref={searchRef}
                  hasClearButton
                  isClearButtonVisible
                  onClear={handleClearSearch}
                  icon={<Magnifier />}
                  appearance={error ? 'error' : 'default'}
                  message="No results found"
                />
              </form>
            </Box>
          </Container>
        </Container>
      </Box>
      <Box mb={DesignTokenSizeEnum.static515}>
        <AccordionGroup>
          {Object.keys(modelFeaturesData).map((title, index) => {
            const sectionData = modelFeaturesData[title];

            return (
              <Accordion
                title={renderTextWithHighlights(title)}
                titleAppearance={TokenTextAppearance.copy200}
                id={formatIdForDOM(title)}
                key={title}
                isOpen={isSpecDetailOpen[index]}
                onToggle={() => handleToggleAccordion(index, title, true)}
              >
                <Box
                  gap={DesignTokenSizeEnum.static400}
                  display="flex"
                  flexDirection="column"
                >
                  {Object.keys(sectionData).map((key: string) => (
                    <ModelFeaturesSection
                      key={key}
                      title={renderTextWithHighlights(key)}
                      specItems={sectionData[key]}
                      activeSlideIndex={activeSlideIndex}
                    />
                  ))}
                </Box>
              </Accordion>
            );
          })}
        </AccordionGroup>
      </Box>
      <Text
        appearance={TokenTextAppearance.copy100}
        color={TokenTextColor.secondary}
      >
        {contentManager.get('terms-conditions-rrp')}
      </Text>
    </div>
  );
};

export default ModelFeatures;
